import { useCallback, useEffect, useMemo, useState } from "react";
import http from "../../../api/apiClient";
import { API_PATH } from "../constants";
import type { Classroom, EntityId, Filters, SchoolCalendarEvent, SchoolYear } from "../types";
import {
  compareClassrooms,
  entityId,
  eventClassroomUuids,
  eventScope,
  getSchoolYearDateRange,
  getStoredSchoolYearUuid,
  isCurrentSchoolYear,
  isSchoolYearActive,
  isSchoolYearInDateRange,
  normalizeList,
  normalizeModalError,
  schoolYearId,
  schoolYearLabel,
  startOfLocalDay,
  storeSchoolYearUuid,
  toTime,
  matchesEventQuery,
} from "../utils";
import { buildYearPlannerMonths } from "../yearPlannerMonths";

interface UseSchoolCalendarDataArgs {
  t: any;
  i18n: any;
  canView: boolean;
}

const emptyFilters: Filters = {
  scope: "",
  classroomUuid: "",
  eventType: "",
  published: "",
  from: "",
  to: "",
  query: "",
};

export const useSchoolCalendarData = ({ t, i18n, canView }: UseSchoolCalendarDataArgs) => {
  const [events, setEvents] = useState<SchoolCalendarEvent[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [selectedSchoolYearUuid, setSelectedSchoolYearUuid] = useState("");
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status?: EntityId; message: string } | null>(null);
  const [filters, setFilters] = useState<Filters>(emptyFilters);

  const sortedClassrooms = useMemo(() => [...classrooms].sort(compareClassrooms), [classrooms]);

  const sortedSchoolYears = useMemo(
    () =>
      [...schoolYears].sort((a, b) => {
        const aStart = getSchoolYearDateRange(a).startDate?.getTime() ?? 0;
        const bStart = getSchoolYearDateRange(b).startDate?.getTime() ?? 0;
        return bStart - aStart;
      }),
    [schoolYears],
  );

  const selectedSchoolYear = useMemo(
    () =>
      sortedSchoolYears.find(
        (year) => String(entityId(year)) === String(selectedSchoolYearUuid),
      ) || null,
    [selectedSchoolYearUuid, sortedSchoolYears],
  );

  const activeSchoolYear = useMemo(
    () => sortedSchoolYears.find(isCurrentSchoolYear) || null,
    [sortedSchoolYears],
  );

  const selectedSchoolYearApiLabel = useMemo(() => {
    if (!selectedSchoolYear) return "";
    const label = schoolYearLabel(selectedSchoolYear);
    const match = label.match(/(\d{4})\D+(\d{4})/);
    if (match) return `${match[1]}-${match[2]}`;

    const { startDate, endDate } = getSchoolYearDateRange(selectedSchoolYear);
    if (startDate && endDate) {
      return `${startDate.getFullYear()}-${endDate.getFullYear()}`;
    }

    return "";
  }, [selectedSchoolYear]);

  const schoolYearGroups = useMemo(() => {
    const recommendedYear = activeSchoolYear || selectedSchoolYear;
    const recommendedId = entityId(recommendedYear);
    const today = startOfLocalDay();

    const startTime = (year: SchoolYear) =>
      getSchoolYearDateRange(year).startDate?.getTime() ?? 0;

    const recommended = recommendedYear ? [recommendedYear] : [];

    const remaining = sortedSchoolYears.filter(
      (year) => String(entityId(year)) !== String(recommendedId),
    );

    const upcoming = remaining
      .filter((year) => {
        const { startDate } = getSchoolYearDateRange(year);
        return Boolean(startDate && startDate > today);
      })
      .sort((a, b) => startTime(a) - startTime(b));

    const archived = remaining
      .filter((year) => {
        const { endDate } = getSchoolYearDateRange(year);
        return Boolean(endDate && endDate < today);
      })
      .sort((a, b) => startTime(b) - startTime(a));

    const uncategorized = remaining
      .filter((year) => !upcoming.includes(year) && !archived.includes(year))
      .sort((a, b) => startTime(b) - startTime(a));

    return {
      recommended,
      upcoming,
      archived: [...archived, ...uncategorized],
    };
  }, [activeSchoolYear, selectedSchoolYear, sortedSchoolYears]);

  const yearClassrooms = useMemo(
    () =>
      sortedClassrooms.filter((classroom) => {
        if (!selectedSchoolYearUuid) return false;
        return String(schoolYearId(classroom)) === String(selectedSchoolYearUuid);
      }),
    [selectedSchoolYearUuid, sortedClassrooms],
  );

  const classroomLookup = useMemo(() => {
    const lookup = new Map<string, Classroom>();
    classrooms.forEach((classroom) => {
      const id = entityId(classroom);
      if (id) lookup.set(String(id), classroom);
    });
    return lookup;
  }, [classrooms]);

  const loadEvents = useCallback(
    async (schoolYearUuid = selectedSchoolYearUuid) => {
      if (!canView) {
        setEventsLoading(false);
        setError({
          status: 403,
          message: t("common.forbidden", { defaultValue: "Geen toegang" }),
        });
        return;
      }

      if (!schoolYearUuid) {
        setEvents([]);
        return;
      }

      setEventsLoading(true);
      setError(null);

      try {
        const { data } = await http.get(API_PATH, {
          params: { schoolYearUuid },
        });
        setEvents(
          normalizeList<SchoolCalendarEvent>(data).filter(
            (event) => String(schoolYearId(event)) === String(schoolYearUuid),
          ),
        );
      } catch (err) {
        const normalized = normalizeModalError(
          err,
          t,
          t("common.load_error", { defaultValue: "Kon data niet laden." }),
        );
        setError({ status: normalized.status, message: normalized.message });
      } finally {
        setEventsLoading(false);
      }
    },
    [canView, selectedSchoolYearUuid, t],
  );

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      setError({
        status: 403,
        message: t("common.forbidden", { defaultValue: "Geen toegang" }),
      });
      return;
    }

    let active = true;

    const loadReferenceData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [classroomsRes, schoolYearsRes] = await Promise.all([
          http.get("/classrooms"),
          http.get("/years"),
        ]);

        if (!active) return;

        const nextClassrooms = normalizeList<Classroom>(classroomsRes.data);
        const nextYears = normalizeList<SchoolYear>(schoolYearsRes.data);
        const stored = getStoredSchoolYearUuid();
        const storedStillExists = nextYears.some(
          (year) => String(entityId(year)) === String(stored),
        );
        const today = startOfLocalDay();

        const dateActiveYear = nextYears.find((year) => {
          const { startDate, endDate } = getSchoolYearDateRange(year);
          return Boolean(startDate && endDate && startDate <= today && today <= endDate);
        });

        const explicitActiveYear = nextYears.find(
          (year) => isSchoolYearActive(year) && !isSchoolYearInDateRange(year, today),
        );

        const nextFutureYear =
          [...nextYears]
            .filter((year) => {
              const { startDate } = getSchoolYearDateRange(year);
              return Boolean(startDate && startDate > today);
            })
            .sort((a, b) => {
              const aStart = getSchoolYearDateRange(a).startDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
              const bStart = getSchoolYearDateRange(b).startDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
              return aStart - bStart;
            })[0] || null;

        const mostRecentYear =
          [...nextYears].sort((a, b) => {
            const aStart = getSchoolYearDateRange(a).startDate?.getTime() ?? 0;
            const bStart = getSchoolYearDateRange(b).startDate?.getTime() ?? 0;
            return bStart - aStart;
          })[0] || null;

        const fallbackYear = dateActiveYear || explicitActiveYear || nextFutureYear || mostRecentYear;
        const nextSelected = storedStillExists ? stored : String(entityId(fallbackYear) || "");

        setClassrooms(nextClassrooms);
        setSchoolYears(nextYears);
        setSelectedSchoolYearUuid(nextSelected);

        if (nextSelected) storeSchoolYearUuid(nextSelected);
      } catch (err) {
        if (!active) return;
        const normalized = normalizeModalError(
          err,
          t,
          t("common.load_error", { defaultValue: "Kon data niet laden." }),
        );
        setError({ status: normalized.status, message: normalized.message });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReferenceData();
    return () => {
      active = false;
    };
  }, [canView, t]);

  useEffect(() => {
    if (loading) return;
    loadEvents(selectedSchoolYearUuid);
  }, [loadEvents, loading, selectedSchoolYearUuid]);

  const handleSchoolYearChange = (value: string) => {
    setSelectedSchoolYearUuid(value);
    storeSchoolYearUuid(value);
    setEvents([]);
    setFilters(emptyFilters);
  };

  const filteredEvents = useMemo(() => {
    const fromTime = filters.from ? toTime(`${filters.from}T00:00`) : null;
    const toEndTime = filters.to ? toTime(`${filters.to}T23:59:59`) : null;

    return events
      .filter((event) => {
        if (filters.scope && eventScope(event) !== filters.scope) return false;
        if (filters.classroomUuid && !eventClassroomUuids(event).includes(String(filters.classroomUuid))) return false;
        if (filters.eventType && event?.eventType !== filters.eventType) return false;
        if (!matchesEventQuery(event, filters.query)) return false;

        if (filters.published !== "") {
          const published = Boolean(event?.published);
          if (filters.published === "true" && !published) return false;
          if (filters.published === "false" && published) return false;
        }

        const startTime = toTime(event?.startDate);
        const endTime = toTime(event?.endDate);
        if (fromTime != null && (endTime ?? startTime ?? 0) < fromTime) return false;
        if (toEndTime != null && (startTime ?? endTime ?? Number.MAX_SAFE_INTEGER) > toEndTime) return false;

        return true;
      })
      .sort((a, b) => (toTime(a?.startDate) ?? 0) - (toTime(b?.startDate) ?? 0));
  }, [events, filters]);

  const yearPlannerMonths = useMemo(
    () => buildYearPlannerMonths({ events: filteredEvents, schoolYear: selectedSchoolYear, language: i18n.language }),
    [filteredEvents, i18n.language, selectedSchoolYear],
  );

  return {
    events,
    classrooms,
    schoolYears,
    sortedClassrooms,
    sortedSchoolYears,
    selectedSchoolYearUuid,
    selectedSchoolYear,
    selectedSchoolYearApiLabel,
    activeSchoolYear,
    schoolYearGroups,
    yearClassrooms,
    classroomLookup,
    eventsLoading,
    loading,
    error,
    filters,
    setFilters,
    filteredEvents,
    yearPlannerMonths,
    loadEvents,
    handleSchoolYearChange,
  };
};
